import type {
  RemoteServerConfig,
  RPCFileInfo,
  RPCRequest,
  RPCResponse,
  RPCResponseExists,
  RPCResponseGet,
  RPCResponseSet,
  RPCResponseStartswith,
} from "@pdfking/content-server/client"
import type { ContentType, VirtualFile } from "@pdfking/pdfengine"
import type { StringGUID } from "@pdfking/tfunc"
import type { FileStorage } from "../types/FileStorage"
import { BlobToUint8, MetaCache } from "@pdfking/compatibility"
import { EncodeServerJWT, RPCEnum } from "@pdfking/content-server/client"
import { Items, MapAsync, StreamCreate } from "@pdfking/tfunc"
import {
  GENERIC_ERROR,
  PERMISSION_ERROR,
} from "@pdfking/translations/enum/errors"
import {
  CreateRemoteFile,
  RequestBlob,
} from "@pdfking/workflow/actions/pdfengine/Functions"
import url_read_tracked from "@pdfking/workflow/utils/url_read_tracked"

const fail = (_rpc: RPCResponse, status: number) => {
  throw status >= 400 && status < 500 ? PERMISSION_ERROR : GENERIC_ERROR
}

export async function ContentStorage(
  config: RemoteServerConfig
): Promise<FileStorage> {
  const store = MetaCache<VirtualFile>(config.db)

  async function infoToVirtualFile(
    key: string,
    info: RPCFileInfo
  ): Promise<VirtualFile> {
    const mirror = await EncodeServerJWT({ enum: RPCEnum.GET, key }, config)
    const existing = await store.get(info.etag)
    const file_meta = { etag: info.etag }

    if (existing) {
      return {
        ...existing,
        file_meta: { ...existing.file_meta, ...file_meta },
      }
    }

    return CreateRemoteFile({
      file_meta,
      guid: info.etag as StringGUID,
      mirrors: [mirror],
      type: info.type as ContentType,
    })
  }

  const rpc = (req: RPCRequest, response?: Response): Promise<RPCResponse> =>
    EncodeServerJWT(req, config)
      .then(url =>
        response
          ? response.blob().then(data => url_read_tracked(url, { data }))
          : fetch(url)
      )
      .then(response =>
        response.json().then(json => ({
          json: json as RPCResponse,
          status: response.status,
        }))
      )
      .then(({ json, status }) => (json.success ? json : fail(json, status)))

  return {
    async clear(): Promise<void> {
      await rpc({
        enum: RPCEnum.CLEAR,
      })
    },
    async delete(key: string): Promise<void> {
      await rpc({
        enum: RPCEnum.DELETE,
        key,
      })
    },
    async exists(key: string): Promise<boolean> {
      const res = (await rpc({
        enum: RPCEnum.EXISTS,
        key,
      })) as RPCResponseExists

      return res.exists
    },
    async get(key: string): Promise<undefined | VirtualFile> {
      const res = (await rpc({
        enum: RPCEnum.GET,
        key,
      })) as RPCResponseGet

      if (res.info) return await infoToVirtualFile(key, res.info)

      return undefined
    },
    async open(names: string[]): Promise<void> {
      await rpc({
        enum: RPCEnum.OPEN,
        names,
      })
    },
    async set(key: string, content: VirtualFile): Promise<void> {
      const res = await RequestBlob(content)
        .then(
          ({ size, stream }) =>
            new Response(StreamCreate(MapAsync(BlobToUint8, stream)), {
              headers: { "content-length": size.toString() },
            })
        )
        .then(
          response =>
            rpc({ enum: RPCEnum.SET, key }, response) as Promise<RPCResponseSet>
        )

      if (res.info && content.file_meta.remote_name) {
        store.set(res.info.etag, content)
      }
    },
    async startswith(prefix: string) {
      const res = (await rpc({
        deep: true,
        enum: RPCEnum.STARTSWITH,
        prefix,
      })) as RPCResponseStartswith

      const files: Record<string, VirtualFile> = {}

      for (const [key, info] of Items(res.files)) {
        files[key] = await infoToVirtualFile(key, info)
      }

      return files
    },
  }
}
